/**
 * 内嵌认证上下文
 * 管理内嵌登录弹窗的显示和状态
 */

import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureOAuth2Token } from '../services/tokenSyncService';
import api from '../services/api';
import endpoints from '../services/endpoints';
import authService from '../services/authService';
import { getAuthorizeUrl } from '../services/oauth2Service';

const EmbeddedAuthContext = createContext();

export const EmbeddedAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentView, setCurrentView] = useState('password-login'); // 当前显示的视图
  const [pendingRequest, setPendingRequest] = useState(null); // 等待认证的请求

  /**
   * 显示登录弹窗
   * @param {Object} options - 选项
   * @param {string} options.view - 初始视图 (password-login, code-login, register, recovery)，可选
   * @param {Function} options.onSuccess - 登录成功回调
   * @param {Function} options.onCancel - 取消回调
   */
  const showLoginModal = useCallback(async (options = {}) => {
    // 简单的防抖/锁机制，避免短时间内多次弹出
    if (window.isLoginRedirecting) return;
    window.isLoginRedirecting = true;
    setTimeout(() => { window.isLoginRedirecting = false; }, 1000);

    // 关闭已存在的登录窗口
    if (window.loginPopup && !window.loginPopup.closed) {
      window.loginPopup.close();
    }

    // 使用 OneAuth 重定向登录，不再显示内嵌弹窗
    const currentUrl = window.location.href;
    
    // 获取 Hydra 授权 URL（这将包含 PKCE 参数和 state）
    const authorizeUrl = await getAuthorizeUrl(currentUrl);
    
    // 计算屏幕中心位置
    const width = 900;
    const height = 648;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`;

    // 打开新窗口进行登录（使用 features 参数使其成为 popup 窗口）
    // 修改窗口名称以避免复用旧的缓存尺寸
    window.loginPopup = window.open(authorizeUrl, 'oauth2_login_popup_v2', features);
  }, []);

  /**
   * 隐藏登录弹窗
   */
  const hideLoginModal = useCallback(() => {
    setIsModalVisible(false);
    setPendingRequest(null);
  }, []);

  /**
   * 切换视图
   * @param {string} view - 视图名称
   */
  const switchView = useCallback((view) => {
    setCurrentView(view);
  }, []);

  /**
   * 登录成功处理
   * @param {Object} result - 登录结果
   */
  const handleLoginSuccess = useCallback(
    async (result) => {
      console.log('? Login successful:', result);

      if (pendingRequest?.onSuccess) {
        pendingRequest.onSuccess(result);
      }

      hideLoginModal();

      // 如果 result 中已经包含 token，说明是 OAuth2 流程返回的，直接使用即可
      // 不需要再调用 ensureOAuth2Token 去换取 (跨域环境下可能会失败)
      if (result?.access_token) {
        console.log('✅ OAuth2 token received directly from login flow');
      } else {
        console.log('🔄 Attempting to get OAuth2 token after login (fallback)...');
        try {
          await ensureOAuth2Token();
          console.log('✅ OAuth2 token acquired');
        } catch (error) {
          console.error('❌ Failed to get OAuth2 token:', error);
        }
      }

      try {
        const profile = await api.get(endpoints.auth.me);
        const userProfile = profile?.user || profile;
        if (userProfile && userProfile.id) {
          authService.saveAuth({ user: userProfile });
        }
      } catch (error) {
        console.warn('Failed to fetch user profile after login:', error);
      }

      if (window.location.pathname === '/login') {
        console.log('?? Navigating to home page...');
        navigate('/', { replace: true });
      }
    },
    [pendingRequest, hideLoginModal, navigate]
  );

  // 监听来自 Popup 的登录成功消息
  useEffect(() => {
    const handleMessage = async (event) => {
      // 安全检查：来源域名
      // 允许 localhost 和 127.0.0.1 互通 (解决开发环境跨域问题)
      const allowedOrigins = [
        window.location.origin,
        'http://localhost:3000',
        'http://127.0.0.1:3000'
      ];

      if (!allowedOrigins.includes(event.origin)) {
        if (event.data?.type === 'LOGIN_SUCCESS') {
           console.warn(`⚠️ Ignored LOGIN_SUCCESS from unauthorized origin: ${event.origin}`, {
             allowedOrigins,
             currentOrigin: window.location.origin
           });
        }
        return;
      }

      if (event.data?.type === 'LOGIN_SUCCESS') {
        console.log('📩 Received LOGIN_SUCCESS from popup');
        const { payload } = event.data;
        
        // 确保 token 已保存 (虽然 popup 可能已保存，但作为双重保障)
        if (payload) {
             if (payload.access_token) localStorage.setItem('syntrust_access_token', payload.access_token);
             if (payload.refresh_token) localStorage.setItem('syntrust_refresh_token', payload.refresh_token);
             if (payload.user) localStorage.setItem('syntrust_user', JSON.stringify(payload.user));
             localStorage.setItem('syntrust_login_status', 'true');
        }

        // 关闭弹窗引用
        if (window.loginPopup) {
            window.loginPopup.close();
            window.loginPopup = null;
        }
        
        // 重置重定向锁
        window.isLoginRedirecting = false;

        // 触发成功回调
        await handleLoginSuccess(payload || {});
        
        // 刷新页面以确保所有状态更新
        window.location.reload();
    }
  };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleLoginSuccess]);

  /**
   * 取消登录
   */
  const handleLoginCancel = useCallback(() => {
    console.log('❌ Login cancelled');

    // 调用取消回调
    if (pendingRequest?.onCancel) {
      pendingRequest.onCancel();
    }

    // 隐藏弹窗
    hideLoginModal();
  }, [pendingRequest, hideLoginModal]);

  const value = {
    // 状态
    isModalVisible,
    currentView,

    // 方法
    showLoginModal,
    hideLoginModal,
    switchView,
    handleLoginSuccess,
    handleLoginCancel,
  };

  return (
    <EmbeddedAuthContext.Provider value={value}>
      {children}
    </EmbeddedAuthContext.Provider>
  );
};

// Hook
export const useEmbeddedAuth = () => {
  const context = useContext(EmbeddedAuthContext);
  if (!context) {
    throw new Error('useEmbeddedAuth must be used within EmbeddedAuthProvider');
  }
  return context;
};

export default EmbeddedAuthContext;
